/* eslint-disable camelcase */
import PropTypes from 'prop-types';
import React from 'react';

import AlertsMenu from '../Menu/AlertsMenu';
import InstallMenu from '../Menu/InstallMenu';
import SensorsMenu from '../Menu/SensorsMenu';
import ManageMenu from '../Menu/ManageMenu';
import MobileMenu from '../Menu/MobileMenu';
import ReportForDates from '../TableActions/ReportForDates';
import SearchItem from '../TableMixin/SearchItem';
import * as settings from '../../constants/SettingsKeys';
import './TableMixin.scss';

const _ = require('lodash');

const oprFields = [
  { field: 'Intensity', opr: 'IntensityOpr' },
  { field: 'ProbabilityLeak', opr: 'ProbabilityLeakOpr' },
  { field: 'Probability', opr: 'ProbabilityOpr' },
  { field: 'DetectedAt', opr: 'DetectedAtOpr' },
  { field: 'gPSstatusRatio', opr: 'gPSstatusRatioOpr' },
  { field: 'CommunicationOKratio', opr: 'CommunicationOKratioOpr' },
];
const datesFields = [
  'DetectedAt',
  'fixDate',
  'ClosureDate',
  'SensorInstallationDate',
  'InsertDate',
  'CreateDate',
  'CreationDate',
  'create_date',
];

const TableMixin = (InnerComponent, SettingsComponent) =>
  class extends React.Component {
    constructor(props) {
      super(props);
      this.toggleSettingsDisplay = this.toggleSettingsDisplay.bind(this);
      this.getStateFromProps = this.getStateFromProps.bind(this);
      this.rowClassNameGetter = this.rowClassNameGetter.bind(this);
      this.onRowClick = this.onRowClick.bind(this);
      this.renderFilters = this.renderFilters.bind(this);
      this.sortTable = this.sortTable.bind(this);
      this.sortTableLocaly = this.sortTableLocaly.bind(this);
      this.processFilters = this.processFilters.bind(this);
      this.cleanEmptyFilters = this.cleanEmptyFilters.bind(this);
      this.filterItems = this.filterItems.bind(this);
      this.getSelectedRow = this.getSelectedRow.bind(this);
      this.getCenteredGoToRow = this.getCenteredGoToRow.bind(this);
      this.updateGoToSelectedRow = this.updateGoToSelectedRow.bind(this);
      this.state = Object.assign(this.getStateFromProps(props), {
        showSettings: false,
        goToRow: null,
        searchText: '',
      });
    }

    cleanEmptyFilters(filters) {
      return filters.filter((filter) => {
        if (filter.op && filter.op === 'range') {
          // For date fields
          const fromValue =
            filter.from != null ? filter.from.toString().trim() : '';
          const toValue = filter.to != null ? filter.to.toString().trim() : '';
          return fromValue.length > 0 || toValue.length > 0;
        } else {
          // For regular fields and other operator fields
          return (
            Array.isArray(filter.value) &&
            filter.value.length > 0 &&
            filter.value.some(
              (val) => val != null && val.toString().trim().length > 0
            )
          );
        }
      });
    }

    processFilters(valuesCopy) {
      let filters = [];
      let defFilters = {};
      let sort = { field: valuesCopy['sortBy'], dir: valuesCopy['sortByDir'] };

      if (valuesCopy == null) {
        return { filters, defFilters, sort };
      }

      defFilters['sortBy'] = valuesCopy['sortBy'];
      defFilters['sortByDir'] = valuesCopy['sortByDir'];
      delete valuesCopy['sortBy'];
      delete valuesCopy['sortByDir'];

      // UI-only toggle, not a server filter
      defFilters['showRanking'] = valuesCopy['showRanking'] || false;
      delete valuesCopy['showRanking'];

      // Process date fields
      datesFields.forEach((field) => {
        var filter = { empty: true, to: false, from: false };
        var toKey = field + '-To';
        var fromKey = field + '-From';
        const toValue = valuesCopy[toKey];
        const fromValue = valuesCopy[fromKey];

        if (toKey in valuesCopy && toValue) {
          filter.to = valuesCopy[toKey];
          filter.empty = false;
          defFilters[toKey] = toValue;
          delete valuesCopy[toKey];
        }
        if (fromKey in valuesCopy && fromValue) {
          filter.from = valuesCopy[fromKey];
          filter.empty = false;
          defFilters[fromKey] = fromValue;
          delete valuesCopy[fromKey];
        }
        if (!filter.empty) {
          delete filter.empty;
          filter.op = 'range';
          filter.field = field;
          filters.push(filter);
        }
      });

      // Process operator fields
      oprFields.forEach((fieldData) => {
        if (fieldData.opr in valuesCopy) {
          var opValue = valuesCopy[fieldData.opr];
          if (typeof opValue === 'object') {
            opValue = opValue.value;
          }
          if (fieldData.field in valuesCopy) {
            const filterObj = {
              field: fieldData.field,
              value: [valuesCopy[fieldData.field]],
              op: opValue,
            };
            filters.push(filterObj);
            defFilters[fieldData.field] = valuesCopy[fieldData.field];
            delete valuesCopy[fieldData.field];
          }
          delete valuesCopy[fieldData.opr];
          defFilters[fieldData.opr] = opValue;
        }
      });

      // Process remaining fields
      Object.keys(valuesCopy).forEach((key) => {
        const values = valuesCopy[key];
        if (values != null) {
          // Ensure values is an array
          const valueArray = Array.isArray(values) ? values : [values];

          // Convert values to appropriate types
          let typedValues;
          if (
            key === 'AlertType' ||
            key === 'AlertState' ||
            key === 'MaterialCode' ||
            key === 'G5DeviceType'
          ) {
            // These fields are numbers
            typedValues = valueArray.map((val) =>
              val !== null && val !== undefined ? Number(val) : null
            );
          } else {
            // For other fields, convert to strings
            typedValues = valueArray.map((val) =>
              val !== null && val !== undefined ? String(val) : ''
            );
          }

          // Filter out null or invalid values
          typedValues = typedValues.filter((val) => val !== null);

          const filterObj = { field: key, value: typedValues };

          if (key === 'LastUninstallReason') {
            filterObj.avoidNulls = true;
          }

          filters.push(filterObj);
          defFilters[key] = values;
        }
      });

      // Clean empty filters
      filters = this.cleanEmptyFilters(filters);

      return { filters, defFilters, sort };
    }

    toggleSettingsDisplay() {
      this.setState({ showSettings: !this.state.showSettings });
    }

    getSelectedRow(items = this.props.items, selectedFeature = this.props.selectedFeature) {
      const { path } = this.props;

      if (!selectedFeature || !Array.isArray(items) || !items.length) {
        return null;
      }

      if (path === 'mobile') {
        return (
          items.filter((item) => item.alert_id == selectedFeature?.alert_id)[0] ||
          null
        );
      }

      return items.filter((item) => item.id == selectedFeature)[0] || null;
    }

    getCenteredGoToRow(selectedRow, items = this.props.items) {
      if (!selectedRow || !Array.isArray(items) || !items.length) {
        return null;
      }

      const selectedIndex = items.indexOf(selectedRow);
      if (selectedIndex < 0) {
        return null;
      }

      const rowHeight = 30;
      const chromeHeight = 93;
      const tableViewportHeight = Math.max(
        rowHeight,
        Number(this.props.pHeight || 0) - chromeHeight
      );
      const visibleRows = Math.max(
        1,
        Math.floor(tableViewportHeight / rowHeight)
      );
      const centeredIndex = selectedIndex - Math.floor(visibleRows / 2);

      return Math.max(0, centeredIndex);
    }

    updateGoToSelectedRow(items = this.props.items, selectedFeature = this.props.selectedFeature) {
      const selectedRow = this.getSelectedRow(items, selectedFeature);
      const goToRow = this.getCenteredGoToRow(selectedRow, items);

      setTimeout(() => {
        this.setState({ goToRow });
      }, 500);
    }

    rowClassNameGetter(rowIndex) {
      const {
        selectedFeature,
        indexMap,
        displayAssessments,
        selectedAssessment,
        path,
        items,
      } = this.props;
      const feature = displayAssessments ? selectedAssessment : selectedFeature;
      let className = '';

      switch (path) {
        case 'alerts':
          if (this.props.alertsContextMenu === 'noise') {
            if (
              feature !== '' &&
              rowIndex !== '' &&
              indexMap &&
              rowIndex == indexMap[feature.ID]
            ) {
              className = 'active-row';
            }
          } else {
            if (
              feature !== '' &&
              rowIndex !== '' &&
              rowIndex == indexMap[feature]
            ) {
              className = 'active-row';
            } else if (this.props.alertsContextMenu === 'alerts') {
              const itrAlert = items[rowIndex];
              if (itrAlert && itrAlert.Burst === 2) {
                className = 'pink-row';
              }
            }
          }
          break;
        case 'couples': {
          const coupleItem = this.props.items[rowIndex];
          if (
            feature !== '' &&
            rowIndex !== '' &&
            indexMap &&
            rowIndex == indexMap[feature]
          ) {
            className = 'active-row';
          }
          if (coupleItem && coupleItem.MissingDevices) {
            className += ' deleted';
          }

          break;
        }
        case 'manage':
          const { manageItem, managementContext } = this.props;
          if (manageItem) {
            const listItems = this.state.searchText
              ? this.filterItems()
              : this.props.items;
            const idKeyName = managementContext === 'Users' ? 'UID' : 'id';
            const index = listItems.findIndex(
              (listItem) => listItem[idKeyName] === manageItem.id
            );

            if (index === rowIndex) {
              className = 'active-row';
            }

            // console.log('row class name getter', rowIndex, index);
          }
          // if (manageItem != null && indexMap[manageItem.id] == rowIndex) {
          //   className = 'active-row';
          // }
          break;

        case 'mobile':
          if (
            feature &&
            rowIndex !== '' &&
            indexMap &&
            rowIndex == indexMap[feature.alert_id]
          ) {
            className = 'active-row';
          }

          break;

        default:
          if (
            feature !== '' &&
            rowIndex !== '' &&
            indexMap &&
            rowIndex == indexMap[feature]
          ) {
            className = 'active-row';
          }
          break;
      }

      return className;
    }

    onRowClick(event, rowIndex, onFailCallBack, additionalData) {
      if (event === 'sensorLink') {
        this.props.onItemClick(rowIndex, onFailCallBack);
      } else if (this.props.path !== 'manage') {
        const feature = this.props.items[rowIndex];

        this.props.onItemClick(feature, onFailCallBack, additionalData);
      } else {
        const feature = this.filterItems()[rowIndex];
        this.props.onItemClick(feature, onFailCallBack, additionalData);
      }
    }

    removeFilter(filter) {
      this.props.removeFilter(filter);
    }

    renderFilters(props) {
      const {
        path,
        alertsContextMenu,
        displayAssessments,
        installContext,
        filters,
        t,
        mobileTasksFilters,
        mobileAlertsFilters,
        mobileDisplayMode,
      } = props;
      let newFilters;
      switch (path) {
        case 'alerts':
          if (alertsContextMenu === 'noise') {
            newFilters = filters.noiseAlerts.filters;
          } else if (alertsContextMenu === 'valve') {
            newFilters = props.valveAlertsFilters;
          } else if (alertsContextMenu === 'burst') {
            newFilters = props.burstAlertsFilters;
          } else {
            newFilters = filters.filters;
          }
          break;
        case 'sensors':
          newFilters = [...filters.sensorsFilters, ...filters.sops.filters];
          break;
        case 'couples':
          if (displayAssessments == false) {
            newFilters = filters.couples.filters;
          }
          break;
        case 'install':
          if (installContext == 'SOPs') {
            newFilters = filters.sops.filters;
          } else if (installContext == 'Sensors') {
            newFilters = [...filters.sensorsFilters, ...filters.sops.filters];
          }
          break;
        case 'mobile':
          switch (mobileDisplayMode) {
            case 'Tasks':
              newFilters = mobileTasksFilters.filters;
              break;
            case 'Alerts':
              newFilters = mobileAlertsFilters.filters;
              break;
          }
        default:
          break;
      }

      let output = dispatchEvent ? null : (
        <span className='filter'>{t('no_filters_applyed')}</span>
      );

      const filterNames = {
        AlertState: t('status'),
        // alert_state: t('status'),
        Priority: t('priority'),
        AlertType: t('type'),
        IntensityOpr: t('intensity_opr'),
        Intensity: t('intensity'),
        ProbabilityOpr: t('probability_opr'),
        ProbabilityLeakOpr: t('probability_leak_opr'),

        WorkArea: t('work_area'),
        RepairCode: t('repair_code'),
        ContractorID: t('contractor_id'),
        DetectedAt: t('detected_at'),
        G5DeviceType: t('hw_type'),
      };

      if (newFilters && newFilters.length) {
        newFilters = newFilters.filter(function (filter) {
          if (filter.op === 'range') {
            const fromValue =
              filter.from != null ? filter.from.toString().trim() : '';
            const toValue =
              filter.to != null ? filter.to.toString().trim() : '';
            return fromValue.length > 0 || toValue.length > 0;
          } else if (filter.value && filter.value.length === 0) {
            return false;
          } else if (filter.field === 'sortBy') {
            return false;
          }
          return true;
        });

        output = newFilters.map((filter, index) => (
          <span className='filter' key={index}>
            {filterNames[filter.field] || t(filter.field)}
            <span
              className='remove-filter'
              onClick={() => this.removeFilter(filter.field)}
            >
              {t('x')}
            </span>
          </span>
        ));
      }
      return output;
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
      this.setState(this.getStateFromProps(nextProps));
    }

    shouldComponentUpdate(nextProps, nextState) {
      return true;
    }

    componentDidMount() {
      const { selectedFeature } = this.props;
      if (!selectedFeature) {
        return;
      }
      this.updateGoToSelectedRow();
    }

    componentDidUpdate(prevProps) {
      const { selectedFeature, showDetails, items, path, installMode, lang } =
        this.props;

      // if (prevProps.lang !== lang) {
      //   this.forceUpdate();
      // }

      switch (path) {
        case 'manage':
          break;
        case 'mobile':
          if (selectedFeature) {
            if (
              selectedFeature !== prevProps.selectedFeature ||
              showDetails !== prevProps.showDetails ||
              items?.length !== prevProps.items.length ||
              path !== prevProps.path ||
              (installMode &&
                installMode.installContext !==
                prevProps.installMode.installContext)
            ) {
              this.updateGoToSelectedRow(items, selectedFeature);
            }
          }
          break;
        default:
          if (
            selectedFeature !== prevProps.selectedFeature ||
            showDetails !== prevProps.showDetails ||
            items.length !== prevProps.items.length ||
            path !== prevProps.path ||
            (installMode &&
              installMode.installContext !==
              prevProps.installMode.installContext)
          ) {
            this.updateGoToSelectedRow(items, selectedFeature);
          }
          break;
      }
    }

    sortTable(field, dir) {
      this.props.sortTable(field, dir);
    }

    sortTableLocaly(field, dir) {
      this.props.sortTableLocaly(field, dir);
      //const { items } = this.props;
      //items.sort((a, b) => {
      //    let bRc = a[field] > b[field] ? 1 : (b[field] > a[field] ? -1 : 0);
      //    return (bRc);
      //});
    }

    getStateFromProps(props) {
      const {
        uiInfo,
        showDetails,
        filters,
        mobileDevicesSort,
        mobileDisplayMode,
      } = props;

      let field;
      let dir;

      switch (props.path) {
        case 'alerts':
          switch (props.alertsContextMenu) {
            case 'alerts':
              field = filters.sort.field;
              dir = filters.sort.dir;
              break;

            case 'noise':
              field = props.noiseAlertsSortParams.field;
              dir = props.noiseAlertsSortParams.dir;
              break;

            case 'burst':
              field = props.burstAlertsSortParams.field;
              dir = props.burstAlertsSortParams.dir;
              break;

            default:
              break;
          }
          break;
        case 'sensors':
          switch (props.sensorsContext) {
            case 'PressureSensors':
              field = filters.pressureSensorsSort.field;
              dir = filters.pressureSensorsSort.dir;
              break;
            default:
              field = filters.sensorsSort.field;
              dir = filters.sensorsSort.dir;
              break;
          }
          break;
        case 'couples':
          field = filters.couples.sort.field;
          dir = filters.couples.sort.dir;
          break;
        case 'manage':
          switch (this.props.managementContext) {
            case 'Customers':
              field = filters.customers.sort.field;
              dir = filters.customers.sort.dir;
              break;
            case 'Projects':
              field = filters.projects.sort.field;
              dir = filters.projects.sort.dir;
              break;
            case 'Users':
              field = filters.users.sort.field;
              dir = filters.users.sort.dir;
              break;
          }
          break;
        case 'mobile': {
          // Only the devices table currently sorts via the header cells. The
          // devices sort stores the column key verbatim, so use it as-is (no
          // camelCase) to keep the active column highlighted across clicks —
          // otherwise sortBy never matches the column and only the first click
          // (which always sorts ascending) appears to work.
          if (mobileDisplayMode === 'Devices') {
            field = mobileDevicesSort?.field;
            dir = mobileDevicesSort?.dir;
          }
          break;
        }
        case 'install': {
          switch (this.props.installContext) {
            case 'SOPs': {
              const sopsSort = filters.sops.sort;
              if (sopsSort) {
                field = sopsSort.field;
                dir = sopsSort.dir;
              }
              break;
            }
            case 'PressureSensors': {
              field = filters.pressureSensorsSort.field;
              dir = filters.pressureSensorsSort.dir;
              break;
            }
            case 'Interferences': {
              const interferencesSort = filters.interferencesSort;
              if (interferencesSort) {
                field = interferencesSort.field;
                dir = interferencesSort.dir;
              }
              break;
            }
            default:
              break;
          }

          break;
        }
        default:
          break;
      }

      const localSortProp = {
        sort: this.sortTableLocaly,
        sortBy: field,
        sortDir: dir,
      };
      const sortProp = {
        sort: this.sortTable,
        sortBy: field,
        sortDir: dir,
      };

      let pWidth = 0.395 * (uiInfo.pageWidth - 10);
      let pHeight = uiInfo.pageHeight;
      if (showDetails) {
        pHeight = 0.494 * uiInfo.pageHeight;
      }

      const panelStyle = { minHeight: 0.507 * uiInfo.pageHeight };

      const renderedFilters = this.renderFilters(props);
      return {
        renderedFilters,
        panelStyle,
        pHeight,
        pWidth,
        sortProp,
        localSortProp,
      };
    }

    filterItems = () => {
      const { items, path } = this.props;
      if (!(path === 'manage')) {
        return items;
      }
      if (!items || items.length === 0) {
        return [];
      }
      const keys = Object.keys(items[0]);

      if (!this.state.searchText || this.state.searchText.trim() === '') {
        return items;
      }

      return items.filter((item) => {
        return keys.some((key) => {
          const val = item[key];
          return (
            val &&
            val
              .toString()
              .toLowerCase()
              .includes(this.state.searchText.toLowerCase())
          );
        });
      });
    };

    render() {
      const { renderedFilters, panelStyle, searchText } = this.state;

      const {
        uiInfo,
        title,
        items,
        displayAssessments,
        refreshData,
        onSearchItem,
        t,
      } = this.props;

      // const language = localStorage.getItem(settings.LANGUAGE);

      const filteredItems = searchText ? this.filterItems() : items;

      console.log('localSortProp', this.state);
      return (
        <div className='alerts' style={panelStyle}>
          <div className='alerts-wrp'>
            <div className='alerts-header'>
              {((this.props.path !== 'manage' &&
                this.props.path !== 'alerts') ||
                (this.props.path === 'alerts' &&
                  this.props.alertsContextMenu !== 'prsAlerts')) && (
                  <div className='actions'>
                    <i
                      className='icon-settings'
                      title={t('filters')}
                      // onClick={this.props.path !== 'mobile' ? this.toggleSettingsDisplay : null}
                      onClick={this.toggleSettingsDisplay}
                    />
                  </div>
                )}
              <div className='actions'>
                <i
                  className='icon-refresh'
                  title={t('reload_data')}
                  onClick={refreshData}
                />
              </div>
              <div className='actions'>
                <SearchItem
                  onChange={(v) => {
                    this.setState({
                      searchText: v,
                    });
                  }}
                  onSearchItem={onSearchItem}
                  path={this.props.path}
                  user={this.props.user}
                  managementContext={this.props.managementContext}
                  installContext={this.props.installContext}
                  sensorsContext={this.props.sensorsContext}
                  alertsContextMenu={this.props.alertsContextMenu}
                  mobileContext={this.props.mobileContext}
                />
              </div>
              <h3 style={{ margin: '10px' }}>{title}</h3>
            </div>
            <div>
              <div className='alerts-filters'>
                {displayAssessments === false &&
                  (this.props.path === 'alerts' &&
                    this.props.alertsContextMenu === 'prsAlerts') === false && (
                    <label>{t('filters')}:</label>
                  )}
                {displayAssessments === false &&
                  (this.props.path === 'alerts' &&
                    this.props.alertsContextMenu === 'prsAlerts') === false &&
                  renderedFilters}
                {this.props.path === 'alerts' &&
                  this.props.alertsContextMenu === 'prsAlerts' && (
                    <ReportForDates done={this.props.loadPressureAlerts} />
                  )}
              </div>
              <InnerComponent
                {...this.state}
                {...this.props}
                items={filteredItems}
                rowClassNameGetter={this.rowClassNameGetter}
                rowClickEvent={this.onRowClick}
              />
              {this.props.path === 'alerts' && (
                <AlertsMenu
                  onChange={this.props.onChangeAlertsContext}
                  user={this.props.user}
                />
              )}
              {this.props.path === 'sensors' && (
                <SensorsMenu onChange={this.props.onChangeSensorsContext} />
              )}
              {this.props.path === 'manage' && (
                <ManageMenu
                  addNew={this.props.addNewManageItem}
                  onChange={this.props.onChangeManageContext}
                />
              )}
              {
                // the path is like a "mode" that part of the URI like "install"
                this.props.path === 'install' && (
                  <InstallMenu
                    addNewInstallItem={this.props.addNewInstallItem}
                    installContext={this.props.installContext}
                    onChange={this.props.onChangeInstallContext}
                  />
                )
              }
              {
                // Add SensorsMenu for sensors path
                this.props.path === 'sensors' && (
                  <SensorsMenu
                    sensorsContext={this.props.sensorsContext}
                    onChange={this.props.onChangeSensorsContext}
                  />
                )
              }
              {
                // the path is like a "mode" that part of the URI like "mobile"
                this.props.path === 'mobile' && (
                  <MobileMenu
                    mobileContext={this.props.mobileContext}
                    onChange={this.props.onChangeMobileDisplayMode}
                  />
                )
              }
              {SettingsComponent != null && (
                <SettingsComponent
                  cleanEmptyFilters={this.cleanEmptyFilters}
                  processFilters={this.processFilters}
                  showSettings={this.state.showSettings}
                  toggleSettingsDisplay={this.toggleSettingsDisplay}
                  uiInfo={uiInfo}
                />
              )}
            </div>
          </div>
        </div>
      );
    }
  };

TableMixin.contextTypes = {
  t: PropTypes.func.isRequired,
};

TableMixin.propTypes = {
  t: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  uiInfo: PropTypes.object.isRequired,
};

export default TableMixin;
